import {
  motion,
  useScroll,
  useTransform,
  useMotionTemplate,
  useReducedMotion } from
"framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";

const AVATAR_URL = "/images/logo-avatar.png";
const BANNER_URL = "/images/hero-image.png";
const CTA_URL = "https://diyb.ca/contact-diyb/";

export default function Hero() {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();

  // The section is taller than the viewport; the inner wrapper is sticky, so the
  // background stays pinned ("fixed") while we scroll through this extra height.
  // Progress 0 = top of section at top of viewport; progress 1 = bottom reaches
  // the top. The sticky child unpins around progress ~0.45 (the 80vh of slack
  // below the viewport), so we land the reveal there and let TheShift follow.
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });

  // Content: words scroll up and fade out before the reveal peaks.
  const textY = useTransform(scrollYProgress, [0, 0.45], ["0%", reduceMotion ? "0%" : "-130%"]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const avatarScale = useTransform(scrollYProgress, [0, 0.3], [1, 0.8]);

  // Background: settles from blurred + dark to sharp + mostly-clear ("into focus").
  const bgScale = useTransform(scrollYProgress, [0, 0.45], reduceMotion ? [1, 1] : [1.12, 1]);
  const blurPx = useTransform(scrollYProgress, [0, 0.45], reduceMotion ? [0, 0] : [8, 0]);
  const bgFilter = useMotionTemplate`blur(${blurPx}px)`;

  // Dark veil masking the image fades away to a light tint as it comes into focus.
  const veilOpacity = useTransform(scrollYProgress, [0, 0.45], [1, 0.15]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative h-[180vh]">

      {/* Sticky stage: pins the background while the content scrolls past it */}
      <div className="sticky top-0 h-screen flex items-center justify-center overflow-hidden">

        {/* Background Image (pinned) — settles from blurred to sharp */}
        <motion.div
          className="absolute inset-0 z-0 bg-cover bg-center will-change-transform"
          style={{ backgroundImage: `url(${BANNER_URL})`, scale: bgScale, filter: bgFilter }} />

        {/* Dark veil — fades out to reveal the image */}
        <motion.div
          className="absolute inset-0 z-0 bg-gradient-to-b from-[#020617]/70 via-[#020617]/80 to-[#020617]"
          style={{ opacity: veilOpacity }} />

        {/* Blueprint Grid */}
        <div className="absolute inset-0 blueprint-grid opacity-20 z-[1]" />

        {/* Content */}
        <motion.div
          className="relative z-10 max-w-5xl mx-auto px-6 text-center"
          style={{ y: textY, opacity: textOpacity }}>

        {/* Avatar */}
        <motion.div
          className="mb-8 flex justify-center"
          style={{ scale: avatarScale }}>

          <motion.img
            src={AVATAR_URL}
            alt="White Beard Solutions"
            data-testid="hero-avatar"
            className="w-28 h-28 md:w-36 md:h-36 rounded-full border-4 border-gold/60 shadow-[0_0_40px_-10px_rgba(212,175,55,0.4)]"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8, type: "spring" }} />

        </motion.div>

        {/* Caption */}
        <motion.p
          className="font-body text-fluid-caption tracking-[0.3em] uppercase text-gold mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}>

          Master Business Craftsman
        </motion.p>

        {/* Main Title */}
        <motion.h1
          className="font-heading font-black text-fluid-hero uppercase tracking-tight mb-6"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.8 }}>

          <span className="text-slate-50">Building</span>
          <br />
          <span className="text-gold-gradient">Smart Digital Doorways</span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          className="font-body text-fluid-body-lg text-slate-400 max-w-2xl mx-auto mb-10"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.6 }}>Smart digital solutions for local business owners in Kingsville, Ontario and the Windsor-Essex Region.

Custom-fit. Solid. Built to last.

        </motion.p>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.6 }}>

          <a href={CTA_URL} target="_blank" rel="noopener noreferrer">
            <Button
              data-testid="hero-cta-button"
              className="bg-gold hover:bg-gold-300 text-navy-500 font-heading font-bold tracking-wider uppercase text-lg px-10 py-6 rounded-sm transition-all duration-300 hover:shadow-[0_0_30px_-5px_rgba(212,175,55,0.5)] animate-glow-pulse">Get Started Today!


            </Button>
          </a>
        </motion.div>

        {/* Scroll Indicator - Integrated in content flow */}
        <motion.div
          className="mt-8 md:mt-12 lg:mt-16 flex flex-col items-center gap-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}>

          <span className="font-body text-xs tracking-widest uppercase text-slate-400">Scroll</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>

            <ArrowDown size={18} className="text-gold/60" />
          </motion.div>
        </motion.div>
        </motion.div>
      </div>
    </section>);

}